import React from 'react'

import { twMerge } from 'tailwind-merge'

import { Skeleton } from '@/components/common/Skeleton'
import { Icon } from '@/components/home/ui/Icon'
import { Surface } from '@/components/home/ui/Surface'

interface StatisticProps {
  label: string
  value: number | string
  children?: React.ReactNode
}

interface StatisticFooterProps {
  children: React.ReactNode
}

export const StatisticRoot: React.FC<StatisticProps> = ({
  label,
  value,
  children,
}) => {
  let icon = null
  const otherChildren: React.ReactNode[] = []

  // Single pass through children
  React.Children.forEach(children, (child) => {
    if (
      React.isValidElement(child) &&
      child.type &&
      (child.type as any).displayName === Icon.displayName
    ) {
      icon = child
    } else {
      otherChildren.push(child)
    }
  })

  return (
    <Surface>
      <Surface.Content className="flex flex-col justify-between h-full">
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <p className="text-sm text-primary-500 dark:text-primary-400 font-medium">
              {label}
            </p>
            <p className="text-2xl font-bold text-primary-800 dark:text-primary-100">
              {value}
            </p>
          </div>
          {icon}
        </div>
        {otherChildren.length > 0 && (
          <div className="mt-4 flex items-center">{otherChildren}</div>
        )}
      </Surface.Content>
    </Surface>
  )
}

interface StatisticFooterProps {
  className?: string
}

const StatisticFooter: React.FC<StatisticFooterProps> = ({
  children,
  className,
}) => {
  return (
    <div className={twMerge('flex items-center', className)}>{children}</div>
  )
}

const StatisticSkeleton: React.FC = () => {
  return (
    <Surface className="flex flex-col items-start justify-between">
      <Surface.Content className="flex flex-col items-start justify-between">
        <div className="flex items-start justify-between w-full gap-2">
          <div>
            <Skeleton className="w-36 h-4 rounded-full" />
            <Skeleton className="w-12 h-6 rounded-lg mt-3" />
          </div>
          <Skeleton className="w-8 h-8 rounded-[5px]" />
        </div>
        <Skeleton className="w-24 h-4 rounded-full mt-6" />
      </Surface.Content>
    </Surface>
  )
}

StatisticFooter.displayName = 'Home.Statistic.Footer'
StatisticSkeleton.displayName = 'Home.Statistic.Skeleton'

export const Statistic = Object.assign(StatisticRoot, {
  Footer: StatisticFooter,
  Icon: Icon,
  Skeleton: StatisticSkeleton,
})
